import React, { useState } from "react";
import {
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import Badge from "../../../components/common/Badge";
import Button from "../../../components/common/Button";

const PaymentUpdateModal = ({
  order,
  paymentStatus,
  onClose,
  onUpdate,
  formatCurrency,
  getPaymentColor,
}) => {
  const [selectedStatus, setSelectedStatus] = useState(
    order.payment?.status || "PENDING"
  );
  const [partialAmount, setPartialAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const netPayable =
    order.orderSummary?.finalTotals?.netPayable ||
    order.orderSummary?.finalTotals?.grandTotal ||
    0;
  const amountPaid = order.orderSummary?.amountPaid || 0;
  const amountDue = order.orderSummary?.amountDue || netPayable;

  const paymentStatuses = [
    {
      value: "PENDING",
      label: "Pending",
      color: "yellow",
      icon: ClockIcon,
      description: "Payment not yet received",
    },
    {
      value: "PARTIAL",
      label: "Partial Payment",
      color: "orange",
      icon: CurrencyRupeeIcon,
      description: "Receive partial payment amount",
    },
    {
      value: "COMPLETED",
      label: "Completed",
      color: "green",
      icon: CheckCircleIcon,
      description: "Full payment received",
    },
    {
      value: "FAILED",
      label: "Failed",
      color: "red",
      icon: XCircleIcon,
      description: "Payment attempt failed",
    },
  ];

  const handleUpdate = async () => {
    setLoading(true);

    const paymentData = {
      paymentStatus: selectedStatus,
      autoProgressOrder: true,
    };

    if (selectedStatus === "PARTIAL" && partialAmount) {
      paymentData.amount = parseFloat(partialAmount);
    } else if (selectedStatus === "COMPLETED") {
      paymentData.amount = netPayable;
    }

    try {
      await onUpdate(paymentData);
      setLoading(false);
      onClose();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Update Payment Status
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XCircleIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Auto-Progression Info */}
          {paymentStatus &&
            order.status === "PENDING" &&
            selectedStatus === "COMPLETED" && (
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-green-900 text-sm mb-1">
                      Order Will Auto-Confirm
                    </p>
                    <p className="text-xs text-green-700">
                      When you mark payment as completed, this order will
                      automatically change from <strong>PENDING</strong> to{" "}
                      <strong>CONFIRMED</strong>.
                    </p>
                  </div>
                </div>
              </div>
            )}

          {/* Warnings */}
          {paymentStatus?.warnings && paymentStatus.warnings.length > 0 && (
            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <ExclamationCircleIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-yellow-900 text-sm mb-1">
                    Warning
                  </p>
                  {paymentStatus.warnings.map((w, i) => (
                    <p key={i} className="text-xs text-yellow-700 mb-1">
                      {w.message}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Current Payment Info */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-100">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 text-xs font-medium">Order Total</p>
                <p className="text-lg font-bold text-gray-900">
                  {formatCurrency(netPayable)}
                </p>
              </div>
              <div>
                <p className="text-gray-600 text-xs font-medium">Amount Paid</p>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(amountPaid)}
                </p>
              </div>
              <div>
                <p className="text-gray-600 text-xs font-medium">Amount Due</p>
                <p className="text-lg font-bold text-red-600">
                  {formatCurrency(amountDue)}
                </p>
              </div>
              <div>
                <p className="text-gray-600 text-xs font-medium">
                  Current Status
                </p>
                <Badge
                  variant={getPaymentColor(order.payment?.status)}
                  size="sm"
                >
                  {order.payment?.status}
                </Badge>
              </div>
            </div>
          </div>

          {/* Payment Status Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Payment Status
            </label>
            <div className="space-y-2">
              {paymentStatuses.map((status) => {
                const Icon = status.icon;
                const isSelected = selectedStatus === status.value;
                return (
                  <button
                    key={status.value}
                    onClick={() => setSelectedStatus(status.value)}
                    className={`w-full p-3 rounded-lg border-2 transition-all flex items-start gap-3 ${
                      isSelected
                        ? `border-${status.color}-500 bg-${status.color}-50 shadow-sm`
                        : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div
                      className={`p-1.5 rounded-lg flex-shrink-0 ${
                        isSelected ? `bg-${status.color}-100` : "bg-gray-100"
                      }`}
                    >
                      <Icon
                        className={`w-4 h-4 ${
                          isSelected
                            ? `text-${status.color}-600`
                            : "text-gray-400"
                        }`}
                      />
                    </div>
                    <div className="flex-1 text-left">
                      <span
                        className={`font-medium text-sm block ${
                          isSelected
                            ? `text-${status.color}-700`
                            : "text-gray-700"
                        }`}
                      >
                        {status.label}
                      </span>
                      {status.description && (
                        <span className="text-xs text-gray-500 block mt-0.5">
                          {status.description}
                        </span>
                      )}
                    </div>
                    {isSelected && (
                      <CheckCircleIcon
                        className={`w-5 h-5 text-${status.color}-600 flex-shrink-0`}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Partial Payment Amount */}
          {selectedStatus === "PARTIAL" && (
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount Paid
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
                  ₹
                </span>
                <input
                  type="number"
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full pl-8 pr-4 py-3 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
                  max={amountDue}
                  min={0}
                  step="0.01"
                />
              </div>
              <p className="text-xs text-orange-700 mt-2 flex items-center gap-1">
                <ExclamationCircleIcon className="w-4 h-4" />
                Maximum: {formatCurrency(amountDue)}
              </p>

              {partialAmount &&
                parseFloat(partialAmount) + amountPaid >= netPayable && (
                  <div className="mt-2 bg-green-100 border border-green-300 rounded p-2">
                    <p className="text-xs text-green-800 font-medium">
                      ✓ This payment will complete the full amount!
                    </p>
                    {order.status === "PENDING" && (
                      <p className="text-xs text-green-700 mt-1">
                        Order will auto-confirm to CONFIRMED status.
                      </p>
                    )}
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex gap-3 rounded-b-xl border-t border-gray-200">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdate}
            disabled={
              loading || (selectedStatus === "PARTIAL" && !partialAmount)
            }
            className="flex-1"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4 animate-spin" />
                Updating...
              </span>
            ) : (
              "Update Payment"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentUpdateModal;
